import { Clipboard } from '@angular/cdk/clipboard';
import { Component, EventEmitter, inject, Input, OnInit, Output, QueryList, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { NgbDropdownModule, NgbTooltip, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe, I18NextService } from 'angular-i18next';
import {
  AccordionComponent,
  AccordionPanelComponent,
  AlertComponent,
  ButtonComponent,
  MbsPopupType,
  ModalService,
  PanelIconHeader,
  TagComponent,
  ToastService,
  TypedTemplateDirective
} from 'mbs-ui-kit';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { BuildsFacade } from 'mbs-ui-shared@facades/builds.facade';
import { Administrator, BuildType, PermissionsEnum } from 'mbs-ui-shared@models';
import Brand from 'mbs-ui-shared@models/Brand';
import { Build, BuildDetails, BuildVersionType, getSupportCompanyMinimalVersion } from 'mbs-ui-shared@models/build';
import { AgentType } from 'mbs-ui-shared@models/Computer';
import { BuildDownloadOptions, BuildDownloadService } from 'mbs-ui-shared@services/build-download.service';
import { BuildService } from 'mbs-ui-shared@services/build-service/build.service';
import { getNormalizedVersion } from 'mbs-ui-shared@utils/get-normalized-version';
import { BehaviorSubject, finalize, noop } from 'rxjs';

import { AsyncPipe, CommonModule } from '@angular/common';

import { CanPipe } from 'libs/ability/src/lib/ability.pipe';
import { HelpLinkPipe } from 'mbs-ui-shared@help/helpLink.pipe';
import { buildImages, buildsThatSupportBranding, buildsThatSupportCommand, CopyingStatus } from './download-build.constants';

@UntilDestroy()
@Component({
  selector: 'mbs-download-build',
  templateUrl: './download-build.component.html',
  imports: [
    AccordionPanelComponent,
    AlertComponent,
    AsyncPipe,
    ButtonComponent,
    CommonModule,
    I18NextPipe,
    NgbDropdownModule,
    NgbTooltipModule,
    TagComponent,
    CanPipe,
    HelpLinkPipe,
    AccordionComponent,
    TypedTemplateDirective,
    PanelIconHeader
  ]
})
export class DownloadBuildComponent implements OnInit {
  private i18nPipe = inject(I18NextPipe);
  private modalService = inject(ModalService);
  private buildService = inject(BuildService);
  private buildDownloadService = inject(BuildDownloadService);
  private buildsFacade = inject(BuildsFacade);
  private toastService = inject(ToastService);
  private clipboard = inject(Clipboard);
  private i18nextService = inject(I18NextService);
  private auth = inject(AuthFacade);
  private ability = inject(AbilityService);

  public readonly buildTypesEnum = BuildType;
  public readonly BUILD_ACTIONS_TRIGGER_BUTTON_TOKEN = 'download-sidepanel-build-actions';
  public readonly copyingStatus = CopyingStatus;
  public readonly alertType = MbsPopupType;
  public readonly PermissionsEnum = PermissionsEnum;
  public readonly buildVersionType = BuildVersionType;
  public readonly elementSelectors = {
    name: {
      [AgentType.Backup]: {
        publicDownloadBtn: 'downloads-panel--backup-tab--public-download-btn',
        noPublicGenerateBtn: 'downloads-panel--backup-tab--no-public-generate-btn',
        makePublicSandbox: 'downloads-panel--backup-tab--make-public-sandbox',
        downloadSandbox: 'downloads-panel--backup-tab--download-sandbox',
        sandboxTag: 'downloads-panel--backup-tab--sandbox-tag',
        betaDownloadBtn: 'downloads-panel--backup-tab--beta-download-btn',
        copyDropdown: 'downloads-panel--backup-tab--copy-dropdown',
        sandBoxCopyDropdown: 'downloads-panel--backup-tab--sandbox-copy-dropdown',
        betaCopyDropdown: 'downloads-panel--backup-tab--beta-copy-dropdown',
        copyLink: 'downloads-panel--backup-tab--copy-link',
        sandBoxCopyLink: 'downloads-panel--backup-tab--sandbox-copy-link',
        betaCopyLink: 'downloads-panel--backup-tab--beta-copy-link',
        copyCommand: 'downloads-panel--backup-tab--copy-command',
        sandBoxCopyCommand: 'downloads-panel--backup-tab--sandbox-copy-command',
        betaCopyCommand: 'downloads-panel--backup-tab--beta-copy-command',
        delete: 'downloads-panel--backup-tab--delete',
        sandBoxDelete: 'downloads-panel--backup-tab--sandbox-delete',
        bsGenerate: 'downloads-panel--backup-tab--bs-generate-btn',
        bsGenerateSandbox: 'downloads-panel--backup-tab--sandbox-bs-generate-btn'
      },
      [AgentType.RMM]: {
        publicDownloadBtn: 'downloads-panel--rmm-tab--public-download-btn',
        noPublicGenerateBtn: 'downloads-panel--rmm-tab--no-public-generate-btn',
        makePublicSandbox: 'downloads-panel--rmm-tab--make-public-sandbox',
        downloadSandbox: 'downloads-panel--rmm-tab--download-sandbox',
        sandboxTag: 'downloads-panel--rmm-tab--sandbox-tag',
        betaDownloadBtn: 'downloads-panel--rmm-tab--beta-download-btn',
        betaCopyDropdown: 'downloads-panel--rmm-tab--beta-copy-dropdown',
        copyLink: 'downloads-panel--rmm-tab--copy-link',
        sandBoxCopyLink: 'downloads-panel--rmm-tab--sandbox-copy-link',
        betaCopyLink: 'downloads-panel--rmm-tab--beta-copy-link',
        copyCommand: 'downloads-panel--rmm-tab--copy-command',
        sandBoxCopyCommand: 'downloads-panel--rmm-tab--sandbox-copy-command',
        betaCopyCommand: 'downloads-panel--rmm-tab--beta-copy-command',
        delete: 'downloads-panel--rmm-tab--delete',
        sandBoxDelete: 'downloads-panel--rmm-tab--sandbox-delete',
        bsGenerate: 'downloads-panel--rmm-tab--bs-generate-btn',
        bsGenerateSandbox: 'downloads-panel--rmm-tab--sandbox-bs-generate-btn'
      },
      [AgentType.RA]: {
        publicDownloadBtn: 'downloads-panel--ra-tab--public-download-btn',
        noPublicGenerateBtn: 'downloads-panel--ra-tab--no-public-generate-btn',
        makePublicSandbox: 'downloads-panel--ra-tab--make-public-sandbox',
        downloadSandbox: 'downloads-panel--ra-tab--download-sandbox',
        sandboxTag: 'downloads-panel--ra-tab--sandbox-tag',
        betaDownloadBtn: 'downloads-panel--ra-tab--beta-download-btn',
        betaCopyDropdown: 'downloads-panel--ra-tab--beta-copy-dropdown',
        copyLink: 'downloads-panel--ra-tab--copy-link',
        sandBoxCopyLink: 'downloads-panel--ra-tab--sandbox-copy-link',
        betaCopyLink: 'downloads-panel--ra-tab--beta-copy-link',
        copyCommand: 'downloads-panel--ra-tab--copy-command',
        sandBoxCopyCommand: 'downloads-panel--ra-tab--sandbox-copy-command',
        betaCopyCommand: 'downloads-panel--ra-tab--beta-copy-command',
        delete: 'downloads-panel--ra-tab--delete',
        sandBoxDelete: 'downloads-panel--ra-tab--sandbox-delete',
        bsGenerate: 'downloads-panel--ra-tab--bs-generate-btn',
        bsGenerateSandbox: 'downloads-panel--ra-tab--sandbox-bs-generate-btn'
      },
      [AgentType.NetworkDiscovery]: {
        publicDownloadBtn: 'downloads-panel--nd-tab--public-download-btn',
        noPublicGenerateBtn: 'downloads-panel--nd-tab--no-public-generate-btn',
        makePublicSandbox: 'downloads-panel--nd-tab--make-public-sandbox',
        downloadSandbox: 'downloads-panel--nd-tab--download-sandbox',
        sandboxTag: 'downloads-panel--nd-tab--sandbox-tag',
        betaDownloadBtn: 'downloads-panel--nd-tab--beta-download-btn',
        betaCopyDropdown: 'downloads-panel--nd-tab--beta-copy-dropdown',
        copyLink: 'downloads-panel--nd-tab--copy-link',
        sandBoxCopyLink: 'downloads-panel--nd-tab--sandbox-copy-link',
        betaCopyLink: 'downloads-panel--nd-tab--beta-copy-link',
        copyCommand: 'downloads-panel--nd-tab--copy-command',
        sandBoxCopyCommand: 'downloads-panel--nd-tab--sandbox-copy-command',
        betaCopyCommand: 'downloads-panel--nd-tab--beta-copy-command',
        delete: 'downloads-panel--nd-tab--delete',
        sandBoxDelete: 'downloads-panel--nd-tab--sandbox-delete',
        bsGenerate: 'downloads-panel--nd-tab--bs-generate-btn',
        bsGenerateSandbox: 'downloads-panel--nd-tab--sandbox-bs-generate-btn'
      },
      deleteConfirmModal: 'downloads-panel--delete-confirm-modal',
      deleteConfirmDeleteBtn: 'downloads-panel--delete-confirm-delete-btn',
      default: {
        publicDownloadBtn: 'downloads-panel--default-tab--public-download-btn',
        noPublicGenerateBtn: 'downloads-panel--default-tab--no-public-generate-btn',
        makePublicSandbox: 'downloads-panel--default-tab--make-public-sandbox',
        downloadSandbox: 'downloads-panel--default-tab--download-sandbox',
        sandboxTag: 'downloads-panel--default-tab--sandbox-tag',
        betaDownloadBtn: 'downloads-panel--default-tab--beta-download-btn',
        betaCopyDropdown: 'downloads-panel--default-tab--beta-copy-dropdown',
        copyLink: 'downloads-panel--default-tab--copy-link',
        sandBoxCopyLink: 'downloads-panel--default-tab--sandbox-copy-link',
        betaCopyLink: 'downloads-panel--default-tab--beta-copy-link',
        copyCommand: 'downloads-panel--default-tab--copy-command',
        sandBoxCopyCommand: 'downloads-panel--default-tab--sandbox-copy-command',
        betaCopyCommand: 'downloads-panel--default-tab--beta-copy-command',
        delete: 'downloads-panel--default-tab--delete',
        sandBoxDelete: 'downloads-panel--default-tab--sandbox-delete',
        bsGenerate: 'downloads-panel--default-tab--bs-generate-btn',
        bsGenerateSandbox: 'downloads-panel--default-tab--sandbox-bs-generate-btn'
      }
    }
  };

  /** Template tokens */
  public readonly newBuildSectionToken: {
    section: BuildDetails;
    isSandbox: boolean;
  };
  public readonly buildDescriptionToken: { build: Build };

  public panelId = null;
  public currentCopingStatus: CopyingStatus = null;
  public copyingTooltipText = new BehaviorSubject(null);
  public downloadBuildInProgress: { sandbox: boolean; notSandbox: boolean } = { sandbox: false, notSandbox: false };
  public showHelp = true;
  public makePublicButtonLoadingId: BuildType;

  /** Build descriptions */
  public windowsSupported = this.i18nextService.t('shared:builds.windows.supported', { returnObjects: true });
  public windowsCanBackup = this.i18nextService.t('shared:builds.windows.can_backup', { returnObjects: true });
  public vmwareSupported = this.i18nextService.t('shared:builds.VMware.supported', { returnObjects: true });
  public linuxDebSupported = this.i18nextService.t('shared:builds.linux_deb.supported', { returnObjects: true });
  public linuxRpmSupported = this.i18nextService.t('shared:builds.linux_rpm.supported', { returnObjects: true });
  public macOsSupported = this.i18nextService.t('shared:builds.mac_os.supported', { returnObjects: true });
  public rmmSupported = this.i18nextService.t('shared:builds.rmm.supported', { returnObjects: true });
  public rmmMacSupported = this.i18nextService.t('shared:builds.rmm_mac_os.supported', { returnObjects: true });
  public connectHostForWindowsSupported = this.i18nextService.t('shared:builds.connectHostForWindows.supported', { returnObjects: true });
  public connectHostForMacSupported = this.i18nextService.t('shared:builds.connectHostForMac.supported', { returnObjects: true });
  public rmmLinuxRpmSupported = this.i18nextService.t('shared:builds.rmm_linux_rpm.supported', { returnObjects: true });
  public rmmLinuxDebSupported = this.i18nextService.t('shared:builds.rmm_linux_deb.supported', { returnObjects: true });
  public macArmSupported = this.i18nextService.t('shared:builds.mac_arm.supported', { returnObjects: true });

  @Input() build: Build;
  @Input() beta: boolean;
  @Input() isSandboxEnabled = false;
  @Input() set openedPanelIds(panelIds: string[]) {
    this.closeOthers && !panelIds?.includes(this.panelId) && this.accordion?.collapseAll();
  }
  @Input() licensedBrandList: Brand[] = [];
  @Input() buildsInProgress?: Build['mbsBuildType'][] = [];
  @Input() currentUser: Administrator;
  @Input() index: number;
  @Input() showDeleteButton = true;

  /** Keep only one build opened */
  @Input() closeOthers = true;

  /** Pass to open build description on component initiate */
  @Input() activeIds = [];

  /** Align content */
  @Input() align: 'left' = null;

  @Input() companyId: string = null;
  @Input() type: AgentType;

  @Output() buildPanelClick = new EventEmitter<{ panelId: string; opening: boolean }>();
  @Output() removeBuild = new EventEmitter<{ build: Build; isSandbox: boolean }>();
  @Output() raActionButtonClick = new EventEmitter<null>();

  @ViewChildren('copyTooltip') copyTooltips: QueryList<NgbTooltip>;
  @ViewChild(AccordionComponent, { static: false }) accordion: AccordionComponent;

  ngOnInit() {
    this.panelId = 'build-item-panel_' + this.build.mbsBuildType;

    this.auth.currentUser$.pipe(untilDestroyed(this)).subscribe((admin) => {
      this.showHelp = admin?.IsProvider || this.ability.can('read', PermissionsEnum.HelpMarketing);
    });
  }

  public onBuildPanelClick({ panelId, event: opening }: { panelId: string; event: boolean }) {
    this.buildPanelClick.emit({ panelId, opening });
  }

  public getIcon(build: Build): string {
    return buildImages[build.buildOs.toLowerCase()];
  }

  public getPublicVersionName(build: Build): string {
    return build.public?.version ? `v${build.public?.version} (${this.i18nPipe.transform('shared:download-build.versions.public')})` : '-';
  }

  public isOneOfTheBuildsIsInProgress(): boolean {
    /*
      Temporary fix. After additional Builder instances on backend side will be launched need to return previous logic:
        1. Replace isBuildInProgress logic with isGenerateButtonInLoadingState.
        2. Delete isGenerateButtonInLoadingState method.
    */
    return !!this.buildsInProgress?.length;
  }

  public isBuildInProgress(build: Build): boolean {
    return this.buildsInProgress?.includes(build.mbsBuildType);
  }

  public onDownloadButtonClick(build: Build, versionType: BuildVersionType): void {
    this.downloadBuildInProgress.notSandbox = true;

    this.buildDownloadService
      .download(build, versionType, this.buildDownloadOptions)
      .pipe(
        finalize(() => (this.downloadBuildInProgress.notSandbox = false)),
        untilDestroyed(this)
      )
      .subscribe();
  }

  public isBuildSupportBranding(build: Build): boolean {
    return buildsThatSupportBranding.includes(build.mbsBuildType);
  }

  public getCopyDropdownE2eKey(versionType: BuildVersionType): string {
    switch (versionType) {
      case BuildVersionType.Sandbox:
        return 'sandBoxCopyDropdown';
      case BuildVersionType.Beta:
        return 'betaCopyDropdown';
      default:
        return 'copyDropdown';
    }
  }

  public getCopyLinkE2eKey(versionType: BuildVersionType): string {
    switch (versionType) {
      case BuildVersionType.Sandbox:
        return 'sandBoxCopyLink';
      case BuildVersionType.Beta:
        return 'betaCopyLink';
      default:
        return 'copyLink';
    }
  }

  public getCopyCommandE2eKey(versionType: BuildVersionType): string {
    switch (versionType) {
      case BuildVersionType.Sandbox:
        return 'sandBoxCopyCommand';
      case BuildVersionType.Beta:
        return 'betaCopyCommand';
      default:
        return 'copyCommand';
    }
  }

  public generateBuild(build: Build, isSandboxBuildRequests = false): void {
    this.buildDownloadService.generateBuild(build, isSandboxBuildRequests).pipe(untilDestroyed(this)).subscribe();
  }

  public onCopyDownloadLinkButtonClick(build: Build, versionType: BuildVersionType): void {
    this.buildDownloadService.copyLink(build, versionType, this.buildDownloadOptions).pipe(untilDestroyed(this)).subscribe();
  }

  public isBuildSupportCommand(build: Build): boolean {
    return buildsThatSupportCommand.includes(build.mbsBuildType);
  }

  public onCopyCommandForDeployBuildClick(build: Build, versionType: BuildVersionType): void {
    this.buildDownloadService.copyCommand(build, versionType, this.buildDownloadOptions).pipe(untilDestroyed(this)).subscribe();
  }

  public onRemoveBuildButtonClick(build: Build, isSandbox: boolean, deleteConfirmTemplate: TemplateRef<any>): void {
    this.modalService
      .open(
        {
          locator: this.elementSelectors.name.deleteConfirmModal,
          header: { title: this.i18nPipe.transform('shared:download-build.confirmBuildDeletionTitle') },
          footer: {
            okButton: {
              text: this.i18nPipe.transform('shared:buttons.delete'),
              type: 'danger',
              e2eLocator: this.elementSelectors.name.deleteConfirmDeleteBtn
            }
          },
          data: {
            context: { build, isSandbox }
          }
        },
        deleteConfirmTemplate
      )
      .then((confirmed) => {
        if (!confirmed) return;

        this.buildService.removeBuild(build, isSandbox).subscribe((res) => {
          this.removeBuildHandle(build, isSandbox);
          this.handleResponse(res);
        });
      })
      .catch(noop);
  }

  public makeBuildPublic(build: Build): void {
    this.makePublicButtonLoadingId = build.mbsBuildType;
    this.buildsFacade
      .makePublic(build)
      .pipe(finalize(() => (this.makePublicButtonLoadingId = null)))
      .subscribe((res) => {
        this.handleResponse(res);
      });
  }

  public getNewBuildSection(build: Build): BuildDetails {
    return this.isSandboxEnabled && build?.sandbox ? build?.sandbox : build?.public;
  }

  public getDisabledByMinimalVersionSupportsCompany(details: { version: string }, buildType: BuildType): boolean {
    if (!this.companyId) return false;

    return getNormalizedVersion(details?.version) < getSupportCompanyMinimalVersion(buildType);
  }

  private get buildDownloadOptions(): BuildDownloadOptions {
    return { companyId: this.companyId, isSandboxEnabled: this.isSandboxEnabled, licensedBrandList: this.licensedBrandList };
  }

  private removeBuildHandle(build: Build, isSandbox: boolean): void {
    this.removeBuild.emit({ build, isSandbox });
  }

  private handleResponse(res): void {
    this.toastService[res.success ? 'success' : 'error'](res.message);
  }
}
